import type { Dispatch, SetStateAction } from 'react'
import type { ControllerRenderProps } from 'react-hook-form'
import type { IProvince } from 'features/Address'
import type { AddressOptionKey, BranchForForm, Field, OptionProps } from 'types'

import React from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Dropdown, Loader } from 'components'
import * as utils from 'helpers/getAddress'

interface IInputProvince {
  provinces: IProvince[]
  isProvincesLoading: boolean
  isProvinceChecking: boolean
  setIsProvinceChecking: Dispatch<SetStateAction<boolean>>
  field: ControllerRenderProps
  handleOptions: (
    provinces: IProvince[],
    name: Field,
  ) => OptionProps<AddressOptionKey>[]
  handleSelected: <T extends { [label: string]: string; id: string }>(
    selectedValue: T,
    name: Field,
  ) => OptionProps<AddressOptionKey> | undefined
  getDistricts: (provinceId: string) => void
  emptyId: { id: string }
}

export const InputProvince: React.FC<IInputProvince> = ({
  provinces,
  isProvincesLoading,
  isProvinceChecking,
  setIsProvinceChecking,
  field,
  emptyId,
  handleOptions,
  handleSelected,
  getDistricts,
}) => {
  const { errors, setValue, watch, trigger } = useFormContext<BranchForForm>()

  const { isSameAsCompany, country, province } = watch([
    'isSameAsCompany',
    'country',
    'province',
  ])
  const { t } = useTranslation(['common'])

  const onChange = (item: OptionProps<AddressOptionKey>) => {
    field.onChange(item.key)
    setValue('district', emptyId)
    setValue('subDistrict', emptyId)
    setValue('postalCode', '')
    getDistricts(item.key.id)
  }

  const onBlur = async () => {
    setIsProvinceChecking(true)
    await trigger('province')
    setIsProvinceChecking(false)
  }

  const isError = utils.handleIsError(
    isSameAsCompany,
    province.id,
    Boolean(errors.province),
  )

  return (
    <Dropdown
      placeholder={t('common:address.province.placeholder')}
      options={handleOptions(provinces, 'province')}
      selected={handleSelected(field.value, 'province')}
      onChange={onChange}
      onBlur={onBlur}
      isError={isError}
      withIcon={(isProvincesLoading || isProvinceChecking) && <Loader />}
      disabled={Boolean(!country.id) || isSameAsCompany}
      fullWidth
      ref={field.ref}
    />
  )
}
